import { createApi } from "@reduxjs/toolkit/query/react";
import {
  baseQueryWithArgs,
  getQueryStringWithCreator,
} from "../utils/reduxHelper";
import { APIS_ENDPOINT } from "../utils/api_constants";
import { clearExpiredDismissalFlags } from "../utils/common";

// Auth level returned by the backend when a creator admin is logged in but
// has not selected a creator to impersonate. Treated as a soft state that
// must not wipe localStorage.username.
const CREATOR_ADMIN_NEEDS_CREATOR = "creator_admin_needs_creator";

// Extracted for direct unit testing — behavior is identical to the inline
// transform previously used on `getCreatorConfig`.
export const transformGetCreatorConfigResponse = (response) => {
  // Only touch localStorage.username when the server gives us a definitive
  // answer. On success we mirror the username; on explicit "no session"
  // (success:true + username:null with an auth_level that indicates the user
  // is unauthenticated) we clear it. On soft states (admin needs to pick a
  // creator, transient success:false payloads) we leave the previous value
  // alone so a single flaky request doesn't log the user out.
  if (response?.success) {
    const nextUsername = response.data?.authenticated_creator_username;
    const authLevel = response.data?.auth_level;
    if (nextUsername) {
      localStorage.setItem("username", nextUsername);
    } else if (authLevel && authLevel !== CREATOR_ADMIN_NEEDS_CREATOR) {
      localStorage.removeItem("username");
    }

    // Clear dismissal flags if platform is no longer expired
    clearExpiredDismissalFlags(response.data?.authorizations);

    return response?.data;
  }
  // success:false is not a definitive auth denial — keep the existing
  // username so the next retry can still send ?creator=<username>.
  return { error: response?.error };
};

export const transformGetCreatorConfigErrorResponse = (error) => {
  // Only wipe on explicit auth failures. Transient 5xx / network errors
  // must not log the user out.
  if (error?.status === 401 || error?.status === 403) {
    localStorage.removeItem("username");
  }
  return error;
};

export const efficientReduxAPI = createApi({
  reducerPath: "efficientReduxAPI",
  baseQuery: baseQueryWithArgs,
  refetchOnFocus: true,
  tagTypes: ["Creator"],
  endpoints: (builder) => ({
    //GET Queries
    checkAuthStatusData: builder.query({
      refetchOnFocus: true,
      query: () => `c/checkAuthStatus`,
      extraOptions: { maxRetries: 5 },
    }),
    getCreatorConfig: builder.query({
      query: () =>
        getQueryStringWithCreator(APIS_ENDPOINT.getCreatorConfig.getEndpoint()),
      providesTags: ["CreatorConfig"],
      extraOptions: { maxRetries: 1 },
      transformResponse: transformGetCreatorConfigResponse,
      transformErrorResponse: transformGetCreatorConfigErrorResponse,
    }),
    getInstagramStoriesTabStatus: builder.query({
      query: () => getQueryStringWithCreator("c/getInstagramStoriesTabStatus"),
      extraOptions: { maxRetries: 1 },
      transformResponse: (response) => {
        if (response?.success === true && response?.data) {
          return response.data;
        }
        return response?.data ?? response;
      },
    }),
    getWrapped25YearRoundup: builder.query({
      query: () => getQueryStringWithCreator(`/yearroundup/wrapped25/`),
      extraOptions: { maxRetries: 1 },
      transformResponse: (response) => {
        return response;
      },
    }),
  }),
});
export const {
  useCheckAuthStatusDataQuery,
  useGetCreatorConfigQuery,
  useLazyGetCreatorConfigQuery,
  useGetInstagramStoriesTabStatusQuery,
  useGetWrapped25YearRoundupQuery,
} = efficientReduxAPI;

export default efficientReduxAPI;
